import { useQuery } from '@apollo/client';
import { Timelapse } from '@mui/icons-material';
import { Button, CircularProgress } from '@mui/material';
import { TimelineList, Widget, WidgetContent, WidgetFooter, WidgetHeader, WidgetIcon } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { TlscpEntityVariant } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useConfig } from '~/components/ConfigProvider';
import { MaskedBiddingAlert } from '~/components/MaskedBiddingAlert/MaskedBiddingAlert';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { useOpenClose } from '~/hooks';

import { BUSINESS_LOG_QUERY } from './BusinessLogQuery.gql';
import { BusinessLogItem } from './components/BusinessLogItem/BusinessLogItem';
import { BusinessLogPopover } from './components/BusinessLogPopover/BusinessLogPopover';
import { useMaskedBiddingBusinessLog } from './hooks/useMaskedBiddingBusinessLog';

const MAX_ITEMS = 5;

export const BusinessLogWidget = () => {
  const { entityId, entity } = useModuleContext();

  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'BusinessFeedWidget' });
  const { t: tCommon } = useTranslation(I18nNamespace.common, { keyPrefix: 'CompetitiveBidding' });
  const { negotiationFeed } = useConfig();
  const { pollInterval } = negotiationFeed;
  const [dialogProps, dialogOpen] = useOpenClose();
  const entityVariant = entity === 'demand' ? TlscpEntityVariant.DEMAND : TlscpEntityVariant.NEGOTIATION_CASE;

  const { isMaskedNegotiationCaseLog } = useMaskedBiddingBusinessLog({ entityId, entityVariant });

  const { data, loading } = useQuery(BUSINESS_LOG_QUERY, {
    pollInterval: pollInterval && !isNaN(pollInterval) ? pollInterval * 1000 : 0,
    variables: {
      entityId,
      entityVariant,
      limit: MAX_ITEMS,
      includeChildren: true,
    },
    skip: isMaskedNegotiationCaseLog,
  });
  const { nodes } = data?.telescope.logEntries || {};
  const totalCount = data?.telescope?.logEntries?.totalCount;
  const hasMore = (totalCount || 0) > MAX_ITEMS;

  return (
    <>
      <Widget>
        <WidgetHeader
          title={t('title')}
          icon={<WidgetIcon icon={<Timelapse fontSize="small" />} />}
          actionsLeft={loading ? <CircularProgress size={20} /> : undefined}
        />
        <WidgetContent>
          {isMaskedNegotiationCaseLog ? (
            <MaskedBiddingAlert message={tCommon('businessLog')} />
          ) : (
            <TimelineList dense items={nodes} renderItem={BusinessLogItem} />
          )}
        </WidgetContent>
        <WidgetFooter>
          <Button variant="text" disabled={!hasMore} onClick={dialogOpen}>
            {t('showAll', { count: totalCount || 0 })}
          </Button>
        </WidgetFooter>
      </Widget>
      <BusinessLogPopover entityId={entityId} entityVariant={entityVariant} {...dialogProps} />
    </>
  );
};
